/**
 * Client-side experiment metric transport for USLegal pages — MEP-1377.
 *
 * The legacy render sends its funnel metrics through a route that has nothing to do
 * with `dataLayer`/GTM: a `GET /api/v1/experiment/track` on the main www host, which
 * the backend turns into an ABTower event. ME-rendered pages ship the
 * `data-send-metric` attributes but nothing that reads them, so `pf_form_get` read
 * 0.0237% against 17.383% on the original arm. This module is the missing consumer.
 *
 * Ported from `legacy/scripts/helpers/tracking.js` and
 * `scripts/helpers/analytics/sendClickstreamEvent.js` in
 * `USLEGALINC/uslegalforms-frontend-marketing`. The wire format is copied exactly —
 * same paths, same query shape, same credentials — so the existing ABTower reports
 * and clickstream pipeline read both arms through one vocabulary with no
 * per-render special-casing.
 */

export const METRIC_ATTRIBUTE = 'data-send-metric';
export const METRIC_PARAMS_ATTRIBUTE = 'data-send-metric-params';
export const CLICKSTREAM_ATTRIBUTE = 'data-clickstream-event';

const EXPERIMENT_TRACK_PATH = '/api/v1/experiment/track';
const CLICKSTREAM_TRACK_PATH = '/api/clickstream/track';

type MetricAttributes = Record<string, string | number | boolean>;

/**
 * Legacy's rule: drop any subdomain, keep the registrable domain, prefix `www`. So
 * `es.uslegalforms.com` and `uslegalforms.com` both resolve to `www.uslegalforms.com`.
 *
 * A hostname with no dot (`localhost`, a container name) has no registrable domain to
 * keep, and rewriting it would produce a host that does not exist — such an origin is
 * returned untouched so local runs fail visibly against their own server instead of
 * silently posting nowhere.
 */
const toWwwOrigin = (protocol: string, hostname: string): string => {
  const labels = hostname.split('.');
  if (labels.length < 2) return `${protocol}//${hostname}`;

  return `${protocol}//www.${labels.slice(-2).join('.')}`;
};

/**
 * Resolves the host that owns the tracking endpoints.
 *
 * Legacy's `getDomainLink()` derives it from `window.location.hostname`: keep the
 * registrable domain, force a `www` prefix. That is correct on the experiment, where
 * nginx serves the ME page from `www.uslegalforms.com` (the proxy matches only
 * `^/form-library/…`, so `/api/…` still reaches the USLegal backend on the same
 * origin). It is wrong on a Herald preview, where the page is served from the render
 * host and the real product URL arrives as `?url=` — the same problem
 * `getEditorBaseUrl` already solves for editor redirects, so the rule is shared.
 *
 * Note that on a preview the request becomes cross-origin and the browser will block
 * it. That is expected: previews are not part of the experiment, and metrics have to
 * be verified on the proxied host.
 */
const getTrackingBaseUrl = (): string => {
  const renderUrl = new URLSearchParams(window.location.search).get('url');
  if (renderUrl) {
    try {
      const { protocol, hostname } = new URL(renderUrl);
      return toWwwOrigin(protocol, hostname);
    } catch {
      // Fall through to the location-derived host below.
    }
  }

  return toWwwOrigin(window.location.protocol, window.location.hostname);
};

/**
 * Legacy gates every metric on `window.isRealVisitor === 1`, which its Blade layout
 * prints from the backend's bot detection. ME never sets that flag, but it does its
 * own detection and already gates GTM on the result, so this reads the same signal
 * rather than introducing a second source of truth — the arms stay equivalent in bot
 * handling without a cross-service change.
 */
const isRealVisitor = (): boolean => {
  const globals = (window as unknown as { __ME_GLOBALS__?: { isBot?: boolean } }).__ME_GLOBALS__;
  return !globals?.isBot;
};

/**
 * Serialises the payload the way legacy's `$.param` does, so extra attributes arrive
 * as `attributes[key]=value`.
 */
const buildQuery = (name: string, attributes: MetricAttributes): string => {
  const params = new URLSearchParams({ name });
  Object.entries(attributes).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    params.append(`attributes[${key}]`, String(value));
  });
  return params.toString();
};

/**
 * Reads the optional extra attributes declared in `data-send-metric-params`.
 * Returns `{}` when the attribute is absent or holds invalid JSON.
 */
export const getMetricParams = (element: Element | null): MetricAttributes => {
  const raw = element?.getAttribute(METRIC_PARAMS_ATTRIBUTE);
  if (!raw) return {};

  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (error) {
    console.error(`Invalid ${METRIC_PARAMS_ATTRIBUTE} JSON:`, raw, error);
    return {};
  }
};

/**
 * Sends one ABTower experiment metric.
 *
 * `keepalive` matters only when the click also navigates: legacy passes it for real
 * anchors and relies on its 700ms redirect delay everywhere else. The USLegal CTAs in
 * ME are buttons and divs that redirect through the same 700ms delay, so the request
 * has time to leave either way — `keepalive` is kept for the anchor case to stay
 * byte-compatible with the legacy behaviour.
 */
export const trackMetric = (
  name: string,
  { attributes = {}, keepalive = false }: { attributes?: MetricAttributes; keepalive?: boolean } = {},
): void => {
  if (!name || !isRealVisitor()) return;

  const url = `${getTrackingBaseUrl()}${EXPERIMENT_TRACK_PATH}?${buildQuery(name, attributes)}`;

  fetch(url, {
    method: 'GET',
    credentials: 'include',
    ...(keepalive ? { keepalive: true } : {}),
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  }).catch(error => console.error('Experiment metric request failed:', name, error));
};

/**
 * Sends one clickstream event — the second, independent analytics channel the legacy
 * footer uses through `data-clickstream-event`. Every link that carries it also
 * navigates, hence `keepalive` by default (legacy's `isExitEvent`).
 */
export const trackClickstreamEvent = (
  eventName: string,
  attributes: Record<string, string> = {},
  { keepalive = true }: { keepalive?: boolean } = {},
): void => {
  if (!eventName || !isRealVisitor()) return;

  fetch(`${getTrackingBaseUrl()}${CLICKSTREAM_TRACK_PATH}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ event_name: eventName, attributes }),
    ...(keepalive ? { keepalive: true } : {}),
  }).catch(error => console.error('Clickstream request failed:', eventName, error));
};
